import { withProps, type El, type Props } from './model'

/**
 * WHAT THIS SCREEN SHOWS = WHAT THE SERVICE LAST SAID + WHAT THIS PERSON HAS DONE SINCE.
 *
 * `server` holds each element as the chat service last returned it (a GET, a write's answer, a frame), replaced only
 * by a HIGHER `v` — the service adds one to `v` on every write, so a late frame can never put an older picture back.
 * Deleted elements stay there as tombstones for the same reason.
 *
 * `pending` holds this person's own changes, property by property, each stamped with the number of the write that
 * carries it. They are drawn on top of `server` until the answer to that write — or a later one — arrives, so a
 * shape being dragged does not jump back to where an echo of an earlier move left it.
 *
 * Pure: the hook (useLiveRows) owns the timing; everything here is a function of its arguments.
 *
 * Not only a board's: any rows the service keeps this way — a board's elements, a page's blocks (page/) — the row and
 * its writable props as type parameters, a board's by default.
 */
export type LiveRow = { id: string; t: string; v?: number; rev?: number; del?: boolean }
export type LiveOp<P extends object = Props> = { id: string; set?: P; del?: true; restore?: true }
export type Pend<P extends object = Props> = {
  set: P
  /** The write that carries each property. */
  seq: Record<string, number>
  /** Deleted here, by write #. */
  del?: number
  /** Brought back here (undo of a delete), by write #. */
  restore?: number
}
export type LiveState<E extends LiveRow = El, P extends object = Props> = { server: Record<string, E>; pending: Record<string, Pend<P>>; rev: number }
export type BoardState = LiveState

export const EMPTY: BoardState = { server: {}, pending: {}, rev: 0 }
/** Nothing held yet, for rows of any kind. */
export const emptyLive = <E extends LiveRow, P extends object>(): LiveState<E, P> => ({ server: {}, pending: {}, rev: 0 })

/** The board as this screen draws it. Deleted elements are left out. */
export function view<E extends LiveRow, P extends object>(s: LiveState<E, P>): Record<string, E> {
  const out: Record<string, E> = {}
  const ids = new Set([...Object.keys(s.server), ...Object.keys(s.pending)])
  for (const id of ids) {
    const base = s.server[id]
    const p = s.pending[id]
    if (p?.del !== undefined && (p.restore === undefined || p.del > p.restore)) continue
    const restored = p?.restore !== undefined
    if (!base || (base.del && restored)) {
      // Made here and not yet confirmed — or brought back here: everything it has is in `set`.
      if (!p || typeof (p.set as { t?: unknown }).t !== 'string') {
        if (base && !base.del) out[id] = base
        continue
      }
      const { del: _gone, ...rest } = (base ?? {}) as E
      out[id] = withProps({ ...rest, id } as E, p.set)
      continue
    }
    if (base.del) continue
    out[id] = p ? withProps(base, p.set) : base
  }
  return out
}

/** What the service said about some elements: each kept only if newer than what is held. */
export function fromServer<E extends LiveRow, P extends object>(s: LiveState<E, P>, els: E[], rev?: number): LiveState<E, P> {
  let server = s.server
  for (const el of els) {
    const cur = server[el.id]
    if (cur && (cur.v ?? 0) >= (el.v ?? 0)) continue
    if (server === s.server) server = { ...server }
    server[el.id] = el
  }
  const r = Math.max(s.rev, rev ?? 0)
  return server === s.server && r === s.rev ? s : { ...s, server, rev: r }
}

/**
 * A full read (the board opened, or the socket came back): it replaces what is held — except what arrived from a
 * write NEWER than the read itself, which the read may simply have been too early to see.
 */
export function fromRead<E extends LiveRow, P extends object>(s: LiveState<E, P>, els: E[], rev: number): LiveState<E, P> {
  const server: Record<string, E> = {}
  for (const el of els) server[el.id] = el
  for (const el of Object.values(s.server)) {
    if ((el.rev ?? 0) > rev && (!server[el.id] || (server[el.id]!.v ?? 0) < (el.v ?? 0))) server[el.id] = el
  }
  return { ...s, server, rev: Math.max(rev, s.rev) }
}

/** This person's own change, numbered `seq` — drawn at once, sent by the hook. */
export function local<E extends LiveRow, P extends object>(s: LiveState<E, P>, ops: Array<LiveOp<P>>, seq: number): LiveState<E, P> {
  const pending = { ...s.pending }
  for (const op of ops) {
    const cur: Pend<P> = pending[op.id] ? { ...pending[op.id]!, set: { ...pending[op.id]!.set }, seq: { ...pending[op.id]!.seq } } : { set: {} as P, seq: {} }
    if (op.del) {
      cur.del = seq
    } else {
      if (op.restore) {
        cur.restore = seq
        delete cur.del
      }
      for (const [k, v] of Object.entries(op.set ?? {})) {
        ;(cur.set as Record<string, unknown>)[k] = v
        cur.seq[k] = seq
      }
    }
    pending[op.id] = cur
  }
  return { ...s, pending }
}

/** The service has answered every write up to `seq`: what they carried is in `server` now (or refused). */
export function acked<E extends LiveRow, P extends object>(s: LiveState<E, P>, seq: number): LiveState<E, P> {
  let changed = false
  const pending: Record<string, Pend<P>> = {}
  for (const [id, p] of Object.entries(s.pending)) {
    const set = {} as P
    const seqs: Record<string, number> = {}
    for (const [k, n] of Object.entries(p.seq)) {
      if (n > seq) {
        ;(set as Record<string, unknown>)[k] = (p.set as Record<string, unknown>)[k]
        seqs[k] = n
      } else changed = true
    }
    const next: Pend<P> = { set, seq: seqs }
    if (p.del !== undefined) {
      if (p.del > seq) next.del = p.del
      else changed = true
    }
    if (p.restore !== undefined) {
      if (p.restore > seq) next.restore = p.restore
      else changed = true
    }
    if (Object.keys(seqs).length || next.del !== undefined || next.restore !== undefined) pending[id] = next
  }
  return changed ? { ...s, pending } : s
}

/** A write about these elements was refused: forget this person's changes to them (the service's picture stands). */
export function forget<E extends LiveRow, P extends object>(s: LiveState<E, P>, ids: string[]): LiveState<E, P> {
  if (!ids.some((id) => s.pending[id])) return s
  const pending = { ...s.pending }
  for (const id of ids) delete pending[id]
  return { ...s, pending }
}

/** Everything still unconfirmed, as ops — what a retry sends after a write failed in transit. */
export function pendingOps<E extends LiveRow, P extends object>(s: LiveState<E, P>): Array<LiveOp<P>> {
  const out: Array<LiveOp<P>> = []
  for (const [id, p] of Object.entries(s.pending)) {
    if (p.del !== undefined && (p.restore === undefined || p.del > p.restore)) {
      out.push({ id, del: true })
      continue
    }
    const set = { ...p.set }
    if (p.restore !== undefined) out.push({ id, restore: true, set })
    else if (Object.keys(set).length) out.push({ id, set })
  }
  return out
}

/**
 * Several writes to one element as one — a drag of three shapes queues [a, b, c, a, b, c, …] — so a request carries
 * each element once. Only ORDER PER ELEMENT matters to the service (each op is applied on its own), and a delete or a
 * restore is never merged across: a change after a delete stays after it.
 */
export function coalesce<P extends object>(ops: Array<LiveOp<P>>): Array<LiveOp<P>> {
  const out: Array<LiveOp<P>> = []
  const open = new Map<string, number>()
  for (const op of ops) {
    const at = open.get(op.id)
    if (!op.del && !op.restore && at !== undefined) {
      const prev = out[at]!
      out[at] = { ...prev, set: { ...(prev.set ?? {}), ...(op.set ?? {}) } as P }
      continue
    }
    out.push(op)
    if (op.del) open.delete(op.id)
    else open.set(op.id, out.length - 1)
  }
  return out
}
